import { useEffect, useState, useCallback } from "react";
import { Loader2, ShieldCheck, Users, UserCog, Hash, ScanFace, KeyRound } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { TeacherScheduleTab } from "@/components/profile/TeacherScheduleTab";
import { EmployeeOverview, EmployeeTimesheet, EmployeeActivity, EmployeeMessages } from "./EmployeeProfileTabs";
import { tabCls } from "./ProfileBits";

const ROLE_TONE = { admin: "bg-blue-100 text-blue-800", staff: "bg-amber-100 text-amber-800", teacher: "bg-emerald-100 text-emerald-800", driver: "bg-violet-100 text-violet-800" };

function QuickBtn({ onClick, Icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 transition">
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  );
}

export default function EmployeeProfileDialog({ userId, onClose, companyType, actions = {}, onOpenStudent, initialTab = "overview" }) {
  const [p, setP] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/officers/${userId}/profile`);
      setP(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load profile");
      onClose();
    }
  }, [userId, onClose]);

  useEffect(() => { load(); }, [load]);

  const u = p?.user;
  const Icon = u?.role === "admin" ? ShieldCheck : u?.role === "staff" ? Users : UserCog;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-4xl h-[88vh] flex flex-col overflow-hidden" data-testid="employee-profile-dialog">
        <DialogHeader className="shrink-0">
          <div className="flex items-start gap-4">
            <div className="h-14 w-14 shrink-0 rounded-xl bg-slate-900 text-white flex items-center justify-center"><Icon className="h-7 w-7" /></div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="font-display text-2xl tracking-tight" data-testid="employee-profile-title">{u?.name || "Loading…"}</DialogTitle>
              <DialogDescription asChild>
                <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-slate-500">
                  {u && <>
                    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${ROLE_TONE[u.role] || "bg-slate-100 text-slate-700"}`}>{u.role}</span>
                    {u.email && <span>{u.email}</span>}
                    {p.open_shift && <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-0.5 font-semibold text-emerald-800"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> Clocked in</span>}
                  </>}
                </div>
              </DialogDescription>
            </div>
          </div>
          {u && (
            <div className="flex flex-wrap gap-2 pt-2" data-testid="employee-profile-actions">
              {actions.setPin && <QuickBtn onClick={() => actions.setPin(u)} Icon={Hash} label={u.has_pin ? "Reset PIN" : "Set PIN"} testid="emp-profile-pin-btn" />}
              {actions.faceLink && u.has_pin && <QuickBtn onClick={() => actions.faceLink(u)} Icon={ScanFace} label={u.face_enrolled ? "Re-enroll face" : "Face enrollment link"} testid="emp-profile-face-btn" />}
              {actions.resetPassword && u.email && <QuickBtn onClick={() => actions.resetPassword(u)} Icon={KeyRound} label="Reset password" testid="emp-profile-reset-btn" />}
            </div>
          )}
        </DialogHeader>

        {!p ? (
          <div className="flex-1 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>
        ) : (
          <Tabs defaultValue={u?.role === "teacher" && companyType === "school" ? initialTab : "overview"} className="mt-2 flex-1 min-h-0 flex flex-col">
            <TabsList className="bg-slate-100 rounded-lg p-1 h-auto flex-wrap justify-start shrink-0">
              <TabsTrigger value="overview" className={tabCls} data-testid="employee-tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="timesheet" className={tabCls} data-testid="employee-tab-timesheet">Timesheet ({p.shifts.length})</TabsTrigger>
              <TabsTrigger value="activity" className={tabCls} data-testid="employee-tab-activity">Activity ({p.visits.length + p.student_events.length})</TabsTrigger>
              <TabsTrigger value="messages" className={tabCls} data-testid="employee-tab-messages">Communication ({p.threads.length + p.sent_messages.length})</TabsTrigger>
              {u?.role === "teacher" && companyType === "school" && <TabsTrigger value="schedule" className={tabCls} data-testid="employee-tab-schedule">Schedule</TabsTrigger>}
            </TabsList>
            <TabsContent value="overview" className="mt-4 flex-1 min-h-0 overflow-y-auto pr-1 data-[state=inactive]:hidden"><EmployeeOverview p={p} companyType={companyType} /></TabsContent>
            {u?.role === "teacher" && <TabsContent value="schedule" className="mt-4 flex-1 min-h-0 overflow-y-auto pr-1 data-[state=inactive]:hidden"><TeacherScheduleTab userId={userId} /></TabsContent>}
            <TabsContent value="timesheet" className="mt-4 flex-1 min-h-0 overflow-y-auto pr-1 data-[state=inactive]:hidden"><EmployeeTimesheet shifts={p.shifts} /></TabsContent>
            <TabsContent value="activity" className="mt-4 flex-1 min-h-0 overflow-y-auto pr-1 data-[state=inactive]:hidden"><EmployeeActivity p={p} /></TabsContent>
            <TabsContent value="messages" className="mt-4 flex-1 min-h-0 overflow-y-auto pr-1 data-[state=inactive]:hidden"><EmployeeMessages p={p} onOpenStudent={onOpenStudent} /></TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
